import { Colors } from "@jsq/ds/styles";
import { DEF_EDGE_OFFSET, DEF_EDGE_WIDTH } from "./constants";
import { EdgeConfig, EdgeStyle, EdgeCoordinates, Node, TreeConfig } from "./types";

/**
 * Check whether the position values are valid and assign the default values for the missing ones
 * @param treeConfig
 * @returns valid TreeConfig object
 */
export const validatePosition = (treeConfig: TreeConfig): TreeConfig => {
  return {
    edgeStart: treeConfig.edgeStart || DEF_EDGE_OFFSET,
    edgeWidth: treeConfig.edgeWidth || DEF_EDGE_WIDTH,
    labelStart: treeConfig.labelStart || 0,
  };
};

/**
 * Set the SVG element bounds to capture the lines to be drawn
 * @param bounds
 * @param svgElement
 */
export const setSVGbounds = (bounds: DOMRect, svgElement: SVGElement) => {
  svgElement.setAttribute("width", bounds.width.toString());
  svgElement.setAttribute("height", bounds.height.toString());
};

/**
 * Set the node edges based on the edge configurations
 * @param bounds
 * @param edgeConfigs
 * @param treeConfig
 */
export const setNodeEdges = (
  bounds: DOMRect,
  edgeConfigs: EdgeConfig[],
  treeConfig: TreeConfig,
) => {
  edgeConfigs.forEach((edgeConfig, index) => {
    const startElement = document.getElementById(edgeConfig.from);
    const endElement = document.getElementById(edgeConfig.to);
    const pathElement = document.querySelector(`#edge_path${index}`);
    const labelContainer = document.querySelector(`#label_container${index}`);
    const labelWrapElement = document.getElementById(`label_wrapper${index}`);
    if (startElement && endElement && pathElement) {
      const coord: EdgeCoordinates = computeCoordinates(
        bounds,
        treeConfig,
        startElement,
        endElement,
        labelWrapElement,
      );
      return applyEdgeSpecs(
        pathElement as SVGPathElement,
        coord,
        edgeConfig.edgeStyle,
        labelContainer as SVGForeignObjectElement,
      );
    }
  });
};

/**
 * Compute the coordinates for the edges and labels
 * @param bounds container bounds
 * @param treeConfig basic configurations for the tree
 * @param startElem first or source node
 * @param endElem second or destination node
 * @param labelWrapper container for the label
 * @returns Returns the coordinates for the edge and label
 */
const computeCoordinates = (
  bounds: DOMRect,
  treeConfig: TreeConfig,
  startElem: HTMLElement,
  endElem: HTMLElement,
  labelWrapElement?: HTMLElement | null,
): EdgeCoordinates => {
  const containerTop = bounds.top;
  const containerStart = bounds.left;

  const startElemCoord = startElem.getBoundingClientRect();
  const endElemCoord = endElem.getBoundingClientRect();
  /**
   * Start coordinates for the edge,
   * x = starting point of the node - starting point of the container + offset value for the edge to start(Based on the current design)
   * y = Bottom point of the node - top point of the container
   * The design is assumed to start from the left side of the node.
   */
  const startCoord = {
    x: startElemCoord.left + treeConfig.edgeStart - containerStart,
    y: startElemCoord.bottom - containerTop,
  };

  /**
   * End coordinates for the edge,
   * x = Starting point of the node - starting point of the container
   * y = Bottom point of the node - half of the height of the node - top point of the container
   */
  const endCoord = {
    x: endElemCoord.left - containerStart,
    y: endElemCoord.bottom - endElemCoord.height / 2 - containerTop,
  };
  /**
   * Label coordinates for the edge,
   * x = Starting point of the label + offset value for the label to start(Based on the current design)
   * y = Index of the edge - vertical distance of the label
   */
  let labelCoord = null;
  if (labelWrapElement) {
    const labelWrapperCoord = labelWrapElement?.getBoundingClientRect();
    labelCoord = labelWrapperCoord
      ? {
          x: startCoord.x + treeConfig.labelStart,
          y: endCoord.y - labelWrapperCoord.height / 2,
        }
      : null;
  }

  return { startCoord, endCoord, labelCoord };
};

const applyEdgeSpecs = (
  path: SVGPathElement,
  coord: EdgeCoordinates,
  edgeStyle?: EdgeStyle,
  labelContainer?: SVGForeignObjectElement,
) => {
  path.setAttribute("stroke", edgeStyle?.color || Colors.Neutral300);
  path.setAttribute("stroke-width", edgeStyle?.thickness?.toString() || "1");
  if (edgeStyle?.dashWidth && edgeStyle?.dashGap) {
    path.setAttribute("stroke-dasharray", `${edgeStyle.dashWidth} ${edgeStyle.dashGap}`);
  } else {
    path.removeAttribute("stroke-dasharray");
  }
  const { startCoord, endCoord, labelCoord } = coord;

  path.setAttribute("d", `M${startCoord.x} ${startCoord.y} V${endCoord.y} H${endCoord.x}`);
  if (labelContainer && labelCoord) {
    labelContainer.setAttribute("x", labelCoord.x.toString());
    labelContainer.setAttribute("y", labelCoord.y.toString());
  }
};

export const edgeConfig = (
  treeNode: Node,
  edgeStyle?: EdgeStyle,
  rootEdgeStyle?: EdgeStyle,
) => {
  /**
   * @param node Flatten the tree structure into the single level array
   * @returns array of nodes
   */
  const flattenTree = (node: Node): Node[] => {
    const tree: Node[] = [
      { ...node, leafNodes: undefined }, // Include the current node, removing leafNodes
      ...(node.leafNodes ? node.leafNodes.flatMap(flattenTree) : []), // Recursively flatten children
    ];
    return tree;
  };

  const flattenTreeNodes = flattenTree(treeNode);
  const rootNodeId: string = treeNode.id;
  const edgeConfigs: EdgeConfig[] = [];
  flattenTreeNodes.forEach((node) => {
    // Check if the node needs a link to the root node and add this edge config
    if (node.rootEdgeLabel) {
      edgeConfigs.push({
        from: `elem-${rootNodeId}`,
        to: `elem-${node.id}`,
        label: node.rootEdgeLabel,
        edgeStyle: rootEdgeStyle,
      });
    }
    // Check if the node has a parent and add the edge config
    if (node.parentId) {
      edgeConfigs.push({
        from: `elem-${node.parentId}`,
        to: `elem-${node.id}`,
        label: node.edgeLabel,
        edgeStyle: edgeStyle,
      });
    }
  });
  return edgeConfigs;
};
